import React from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { errorToast, successToast } from "../../notifications/toast";
import { useDispatch } from "react-redux";
import { editInventoryAction } from "../../../redux/actions/inventoryAction";

const EditInventoryModal = ({ isOpen, onClose, product, reload }) => {
    const dispatch = useDispatch();
    const initialValues = {
        id: product.id,
        name: product?.product?.name || "",
        category: product?.product?.category?.name || "",
        quantity: product?.quantity || "",
        uom: product?.uom || "",
        pts: product?.pts || "",
        ptr: product?.ptr || "",
        mrp: product?.mrp || "",
        batchNumber: product?.batch_number || "",
    };

    const validationSchema = Yup.object({
        quantity: Yup.number()
            .typeError("Quantity must be a number")
            .integer("Quantity must be an integer")
            // .positive("Quantity must be positive")
            .required("Quantity is required"),
        pts: Yup.number()
            .typeError("Unit Pts Price must be a number")
            .positive("Unit Pts Price must be positive")
            .required("Unit Pts Price is required"),
        ptr: Yup.number()
            .typeError("Unit Ptr Price must be a number")
            .positive("Unit Ptr Price must be positive")
            .required("Unit Ptr Price is required"),
        mrp: Yup.number()
            .typeError("MRP must be a number")
            .positive("MRP must be positive")
            .required("MRP is required"),
        // batchNumber: Yup.string().required("Batch Number is required"),
    });

    const handleSubmit = (values) => {
        dispatch(editInventoryAction(values, onSuccess, onError))
    }

    const onSuccess = (data) => {
        successToast(data.data.message);
        onClose();
        reload()
    }

    const onError = (data) => {
        const errorMsg = data.data.message || "Unable to fetch try later";
        errorToast(errorMsg)
    }

    return (
        <Transition appear show={isOpen} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto flex items-center justify-center p-4">
                    <Dialog.Panel className="bg-white rounded-lg p-6 shadow-lg max-w-md w-full">
                        <Dialog.Title className="text-lg font-medium leading-6 text-primary">
                            Update Inventory
                        </Dialog.Title>
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                            enableReinitialize
                        >
                            {() => (
                                <Form>
                                    {/* Product Name (Read-only) */}
                                    <div className="mt-4">
                                        <label htmlFor="name" className="block text-start">
                                            Product Name
                                        </label>
                                        <div className="input-field w-full mt-1 bg-gray-100 cursor-not-allowed p-2 rounded">
                                            {initialValues.name}
                                        </div>
                                    </div>

                                    {/* Category (Read-only) */}
                                    <div className="mt-4">
                                        <label htmlFor="category" className="block text-start">
                                            Category
                                        </label>
                                        <div className="input-field w-full mt-1 bg-gray-100 cursor-not-allowed p-2 rounded">
                                            {initialValues.category}
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <label htmlFor="uom" className="block text-start">
                                            Uom
                                        </label>
                                        <div className="input-field w-full mt-1 bg-gray-100 cursor-not-allowed p-2 rounded">
                                            {initialValues.uom}
                                        </div>
                                    </div>

                                    {/* Quantity */}
                                    <div className="mt-4">
                                        <label htmlFor="quantity" className="block text-start">
                                            Quantity
                                        </label>
                                        <Field
                                            type="number"
                                            name="quantity"
                                            id="quantity"
                                            className="input-field w-full mt-1"
                                            placeholder="Enter quantity"
                                        />
                                        <ErrorMessage name="quantity" component="div" className="text-red-500 text-sm text-start" />
                                    </div>

                                    {/* Unit Pts Price */}
                                    <div className="mt-4">
                                        <label htmlFor="pts" className="block text-start">
                                            PTS
                                        </label>
                                        <Field
                                            type="text"
                                            name="pts"
                                            id="pts"
                                            className="input-field w-full mt-1"
                                            placeholder="Enter unit price"
                                        />
                                        <ErrorMessage name="pts" component="div" className="text-red-500 text-sm text-start" />
                                    </div>

                                    {/* Unit Ptr Price */}
                                    <div className="mt-4">
                                        <label htmlFor="ptr" className="block text-start">
                                            PTR
                                        </label>
                                        <Field
                                            type="text"
                                            name="ptr"
                                            id="ptr"
                                            className="input-field w-full mt-1"
                                            placeholder="Enter unit price"
                                        />
                                        <ErrorMessage name="ptr" component="div" className="text-red-500 text-sm text-start" />
                                    </div>

                                    {/* MRP */}
                                    <div className="mt-4">
                                        <label htmlFor="mrp" className="block text-start">
                                            MRP
                                        </label>
                                        <Field
                                            type="text"
                                            name="mrp"
                                            id="mrp"
                                            className="input-field w-full mt-1"
                                            placeholder="Enter MRP"
                                        />
                                        <ErrorMessage name="mrp" component="div" className="text-red-500 text-sm text-start" />
                                    </div>

                                    {/* Batch Number */}
                                    <div className="mt-4">
                                        <label htmlFor="batchNumber" className="block text-start">
                                            Batch Number
                                        </label>
                                        <Field
                                            type="text"
                                            name="batchNumber"
                                            id="batchNumber"
                                            className="input-field w-full mt-1"
                                            placeholder="Enter batch number"
                                        />
                                        <ErrorMessage name="batchNumber" component="div" className="text-red-500 text-sm text-start" />
                                    </div>

                                    <div className="mt-6 flex justify-between gap-3">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="bg-gray-400 text-white px-4 py-2 rounded-md hover:bg-gray-500"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </Dialog.Panel>
                </div>
            </Dialog>
        </Transition>
    );
};

export default EditInventoryModal;
